import { authConfig } from "@/config/auth-config";

/**
 * Resolve a `callbackUrl` for `next-auth/react`'s `signIn()`.
 *
 * Threat model: pages that bounce users through `/login` (unsubscribe flow,
 * login-required pages) commonly carry the original destination in a
 * `?redirect=` query param. Without validation, an attacker can craft
 * `?redirect=https://evil.com` to phish users from our domain. We only
 * forward URLs that resolve to our own origin (absolute same-origin OR
 * relative paths starting with a single `/`), rejecting protocol-relative
 * (`//evil.com`) and non-http schemes (`javascript:`).
 *
 * Resolution order:
 *  1. `requestedRedirect` if it is a safe same-origin URL.
 *  2. `fallbackUrl` if it is a safe same-origin URL (typically `window.location.href`).
 *  3. `authConfig.normalUserCallbackUrl` (`/painel`) as the ultimate default.
 */
export function getSafeCallbackUrl({
  requestedRedirect,
  fallbackUrl,
  origin,
}: {
  requestedRedirect: string | null;
  fallbackUrl: string;
  origin: string;
}): string {
  if (isSafeUrl(requestedRedirect, origin)) {
    return requestedRedirect as string;
  }
  if (isSafeUrl(fallbackUrl, origin)) {
    return fallbackUrl;
  }
  return authConfig.normalUserCallbackUrl;
}

function isSafeUrl(url: string | null | undefined, origin: string): boolean {
  if (!url) {
    return false;
  }
  // Protocol-relative URLs ("//evil.com") bypass origin checks in some parsers
  // and must be rejected explicitly.
  if (url.startsWith("//")) {
    return false;
  }
  try {
    const parsed = new URL(url, origin);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return false;
    }
    return parsed.origin === origin;
  } catch {
    return false;
  }
}
